import React from 'react';
import { X, ArrowRight, ArrowLeft, Loader, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const UpgradeToInmobiliariaLayout = ({
  currentStep,
  totalSteps,
  children,
  handleNext,
  handleBack,
  handleFinish,
  loading,
  title,
  errors
}) => {
  const navigate = useNavigate();

  const StepIndicator = () => {
    return (
      <div className="flex items-center justify-center mb-10">
        <div className="flex items-center space-x-3 sm:space-x-4">
          {[...Array(totalSteps)].map((_, index) => {
            const stepNumber = index + 1;
            const isActive = stepNumber === currentStep;
            const isCompleted = stepNumber < currentStep;
            
            return (
              <React.Fragment key={stepNumber}>
                {/* Connector line */}
                {index > 0 && (
                  <div 
                    className={`hidden sm:block h-1 w-12 rounded-full transition-all duration-300 ${
                      isCompleted ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  />
                )}
                
                {/* Step circle */}
                <div className="flex flex-col items-center">
                  <div 
                    className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
                      isActive 
                        ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md' 
                        : isCompleted 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <span className="text-sm font-medium">{stepNumber}</span>
                    )}
                  </div>
                  <span className={`mt-2 text-xs ${
                    isActive ? 'text-indigo-600 font-medium' : 'text-gray-500'
                  }`}>
                    {stepNumber === 1 && 'Información'}
                    {stepNumber === 2 && 'Servicios'}
                    {stepNumber === 3 && 'Plan'}
                  </span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white py-16 px-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`
          mx-auto bg-white rounded-2xl shadow-xl p-8 
          ${currentStep === 3 ? 'max-w-6xl' : 'max-w-2xl'}
        `}
      >
        {/* Cabecera */}
        <div className="flex justify-between items-center mb-8">
          <motion.button 
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate('/mi_perfil')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </motion.button>
          <motion.h1 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-700"
          >
            {title}
          </motion.h1>
          <div className="w-10"></div> {/* Espaciador para centrar el título */}
        </div>

        {/* Indicador de pasos */}
        <StepIndicator />

        {/* Contenido principal */}
        <motion.div 
          className={`${currentStep === 3 ? 'max-w-6xl' : 'max-w-xl'} mx-auto`}
          key={currentStep}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>

        {/* Errores */}
        {errors && Object.keys(errors).length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 shadow-sm"
          >
            {Object.values(errors).map((error, index) => (
              <p key={index} className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                {error}
              </p>
            ))}
          </motion.div>
        )}

        {/* Navegación */}
        <div className="flex justify-between mt-10">
          {currentStep > 1 ? (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              onClick={handleBack}
              className="px-6 py-3 bg-white border border-indigo-200 text-indigo-600 rounded-xl hover:bg-indigo-50 transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Anterior
            </motion.button>
          ) : (
            <div></div> // Espaciador vacío
          )}

          {currentStep < totalSteps ? (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              onClick={handleNext}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all flex items-center gap-2"
            >
              Siguiente 
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          ) : (
            <motion.button
              whileHover={!loading ? { scale: 1.05 } : {}}
              whileTap={!loading ? { scale: 0.95 } : {}}
              type="button"
              onClick={handleFinish}
              disabled={loading}
              className={`px-6 py-3 rounded-xl ml-auto transition-all flex items-center gap-2 ${
                loading 
                  ? 'bg-gray-400 text-white cursor-not-allowed' 
                  : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-lg'
              }`}
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  Finalizar
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </motion.button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default UpgradeToInmobiliariaLayout;